import { useEffect, useState, type ReactNode } from 'react';
import { Ban, CheckCircle2, CircleSlash, Clock3, FileText, Inbox } from 'lucide-react';
import type { PaigeInboundMessage } from './paige.global';
import { outcomeLine, outcomeTag, shownOutcome, skipSentence } from './email-words';

/**
 * The recent-messages tab: what the address received, newest first, one row each with a mark for
 * what it came to (documents, waiting, or left alone), and why each other part was left out.
 */
export function EmailMessages({ sourceId }: { sourceId: string }): ReactNode {
  const [messages, setMessages] = useState<PaigeInboundMessage[] | null>(null);
  useEffect(() => {
    const channel = window.paigeWorkbench?.email;
    if (!channel) return;
    let live = true;
    channel
      .messages(sourceId)
      .then((rows) => live && setMessages(rows))
      .catch(() => live && setMessages([]));
    return () => {
      live = false;
    };
  }, [sourceId]);
  if (messages === null) return <p className="paige-email-sheet-quiet">Reading…</p>;
  if (messages.length === 0)
    return (
      <div className="paige-email-sheet-empty">
        <Inbox size={34} strokeWidth={1.4} aria-hidden="true" />
        Nothing received yet. Send a test to see it here.
      </div>
    );
  return (
    <ul className="paige-email-sheet-messages">
      {messages.map((m) => (
        <Message key={m.id} message={m} />
      ))}
    </ul>
  );
}

const waiting = new Set([
  'fetching',
  'queued',
  'deferred',
  'held',
  'job_off',
  'waiting_for_credits',
]);

/** The row's mark: documents made, still waiting, or nothing taken. */
function Mark({ outcome }: { outcome: string }): ReactNode {
  if (outcome === 'accepted') return <CheckCircle2 size={18} className="is-done" />;
  if (waiting.has(outcome)) return <Clock3 size={18} className="is-waiting" />;
  return <CircleSlash size={18} className="is-none" />;
}

/** One message: its mark, subject, sender and time, its files as chips, and a tag for what it came to. */
function Message({ message }: { message: PaigeInboundMessage }): ReactNode {
  const when = new Date(message.receivedAt).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
  const outcome = shownOutcome(message.outcome, message.receivedAt);
  const line = outcomeLine(outcome);
  return (
    <li>
      <span className="paige-email-sheet-row-icon" aria-hidden="true">
        <Mark outcome={outcome} />
      </span>
      <span className="paige-email-sheet-message">
        <strong>{message.subject || '(no subject)'}</strong>
        <small>
          {message.sender || 'an unknown sender'} · {when}
        </small>
        <Files message={message} />
      </span>
      <span className={`paige-email-sheet-tag is-${tone(outcome)}`} title={`It ${line}.`}>
        {outcomeTag(outcome)}
        <span className="sr-only">: it {line}</span>
      </span>
    </li>
  );
}

const tone = (outcome: string): string =>
  outcome === 'accepted' ? 'done' : waiting.has(outcome) ? 'waiting' : 'none';

/** The message's files: the ones that became documents, then the ones left out, each with why. */
function Files({ message }: { message: PaigeInboundMessage }): ReactNode {
  if (message.kept.length === 0 && message.skipped.length === 0) return null;
  return (
    <span className="paige-email-sheet-files">
      {message.kept.map((name) => (
        <span key={`kept-${name}`} className="paige-email-sheet-file">
          <FileText size={13} aria-hidden="true" />
          {name}
        </span>
      ))}
      {message.skipped.map((part, i) => (
        <span
          key={`skip-${part.name}-${i}`}
          className="paige-email-sheet-file is-left"
          title={`Left out: ${skipSentence(part.reason)}`}
        >
          <Ban size={13} aria-hidden="true" />
          {part.name}
          <span className="sr-only">, left out: {skipSentence(part.reason)}</span>
        </span>
      ))}
    </span>
  );
}
